import { defineComponent as _defineComponent } from 'vue'
import {
  computed,
  defineAsyncComponent,
  resolveDynamicComponent,
  ref,
} from 'vue'
import { useRouter } from 'vue-router'

import { geoApi } from '@/services/geo.service'
import { accountApi } from '@/services/account.service'
import {
  useSubmit,
  catchFormErrors,
  defaultValidatorErrorsParser,
} from '@/composables/useSubmit'
import { useModalOpener } from '@/composables/useModalOpener'

import useGlobal from '@/composables/useGlobal'
import useField from '@/composables/useField'
import { useSelectsDynamicOptions } from '@/composables/useSelectsDynamicOptions'
import { composeTransformers, simplifiers } from '@/utils/transformers'
import { GtagEvents } from '@/const'

import { registrationApi } from '@/services/registration.service'

const CUSTOMER_TYPE = 'customer'

export default /*@__PURE__*/_defineComponent({
  __name: 'SignUp',
  props: {
  loginUrl: String,
  indexPageUrl: String,
  referer: String
},
  setup(__props, { expose: __expose }) {
  __expose();

const TokenConfirmationCheck = defineAsyncComponent(
  () => import('@app/token/ConfirmationCheck.vue')
)
const UiMessageModal = defineAsyncComponent(
  () => import('@app/ui/modals/UiMessageModal.vue')
)
const router = useRouter()

const profileTransformFrom = composeTransformers(
  simplifiers.simple('email'),
  simplifiers.simple('password'),
  simplifiers.simple('tokenId'),
  simplifiers.simple('firstName'),
  simplifiers.simple('lastName'),
  simplifiers.simple('middleName'),
  simplifiers.simple('companyName'),
  simplifiers.simple('phone'),
  simplifiers.selectUuid('communicationLanguage'),
  simplifiers.simple('edrpou'),
  simplifiers.simple('website'),
  simplifiers.selectUuid('country'),
  simplifiers.same('location'),
  simplifiers.same('recaptcha'),
  simplifiers.simple('referer')
)

const { $t } = useGlobal()
const userAgreement = window.user_agreement
const privacyPolicy = window.privacy_policy

const props = __props

const formRef = ref(null)

const form = ref({
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  website: '',
  edrpou: '',
  companyName: '',
  country: null,
  location: null,
  tokenId: undefined,
  password: '',
  recaptcha: '',
  referer: props.referer,
})
const captchaRef = ref(null)

const USER_ROLE_MAP = [
  {
    title: $t('Agent'),
    resourceName: 'agent',
    gtagEvent: GtagEvents.agent_registration,
    gtagEventForReferral: GtagEvents.referral_agent_registration,
    id: 'agent',
  },
  {
    title: $t('Dealer'),
    resourceName: 'dealer',
    gtagEvent: GtagEvents.dealer_registration,
    gtagEventForReferral: GtagEvents.referral_dealer_registration,
    id: 'dealer',
  },
  {
    title: $t('Customer'),
    resourceName: 'customer',
    gtagEvent: GtagEvents.customer_registration,
    gtagEventForReferral: GtagEvents.referral_customer_registration,
    id: CUSTOMER_TYPE,
  },
]

const currentUserType = ref(USER_ROLE_MAP[0])

const currentResource = computed(() => {
  return registrationApi[currentUserType.value.resourceName]
})

function onChangeRole(role) {
  currentUserType.value = role
}

const saveCaptcha = async (val, control, action) => {
  form.value.recaptcha = val
  // await request(form.value, control)
  control.setFieldError('nonFieldErrors', '')
  await catchFormErrors(
    send({
      ...form.value,
      token: form.value.email,
    }),
    control,
    defaultValidatorErrorsParser
  )
    .then(async (res: Response | { id: string }) => {
      if (res && (res as { id: string })?.id) {
        await action((res as { id: string }).id)
      } else {
        throw res
      }
      captchaRef.value.reset()
    })
    .catch((e) => {
      captchaRef.value.reset()
      console.warn(e)
    })
}

const isLoad = ref(false)

const getCaptcha = async () => {
  await captchaRef.value.execute()
}

const validateForm = async (f, action, control) => {
  const { valid } = await f()
  if (valid) {
    try {
      isLoad.value = false
      await action()
    } catch (e) {
      console.warn(e)
    }
  }
}

const currentCountryCode = window.country_code

const {
  loading: countriesLoading,
  list: countriesList,
  load: countriesLoad,
  debounceSearch: countriesSearch,
  get: countriesGet,
} = useSelectsDynamicOptions(geoApi.countriesList)
countriesLoad()

const {
  loading: languagesLoading,
  list: languagesList,
  load: languagesLoad,
} = useSelectsDynamicOptions(accountApi.communicationLanguages)
languagesLoad()

const phoneRules = computed(() => {
  return `required|realPhone:${form.value.phone}`
})

const isNotRequiredRestField = computed(() => {
  if (currentUserType.value.id === CUSTOMER_TYPE) {
   return [
    'companyName',
    'edrpou',
    'website',
   ] 
  }
  return []
})

const FIELDS = ref([
  useField('email', $t('Email address'), {
    rules: 'required|email',
    type: 'email',
  }),
  useField('password', $t('Password'), { type: 'password', rules: 'required' }),
  useField('firstName', $t('First Name'), { rules: 'required' }),
  useField('lastName', $t('Last Name'), { rules: 'required' }),
  useField('phone', $t('Phone'), {
    rules: phoneRules.value,
    component: resolveDynamicComponent('DPhone'),
    'default-country-code': currentCountryCode,
    'no-example': true,
  }),
  useField('country', $t('Country'), {
    rules: 'required',
    'label-by': 'title',
    'track-by': 'id',
    'allow-empty': false,
    'internal-search': false,
    searchable: true,
    loading: countriesLoading,
    options: countriesList,
    onSearchChange: (e) => countriesSearch(e),
    onGetItems: countriesGet.bind(null),
    component: resolveDynamicComponent('DSelect'),
  }),
  useField('location', $t('Address'), {
    rules: 'required',
    size: '12',
    'start-icon': 'i-search',
  }),
  // useField('communicationLanguage', $t('Language'), {
  //   rules: 'required',
  //   'label-by': 'title',
  //   'track-by': 'id',
  //   'allow-empty': false,
  //   'start-icon': 'i-global-search',
  //   'icon-size': ['lg'],
  //   'internal-search': true,
  //   loading: languagesLoading,
  //   options: languagesList,
  //   component: resolveDynamicComponent('DSelect'),
  // }),
  useField('companyName', $t('Company name'), { rules: 'required' }),
  useField('edrpou', $t('RNOCPP / EDRPOU'), { rules: 'required' }),
  useField('website', $t('Website'), {
    rules: 'required',
    size: '12',
  }),
])

const request = (sendData = form.value, control = formRef.value) => {
  isLoad.value = true
  return useSubmit(send(sendData), control)
}
const captchaConfirmedRef = ref(null)
const redirect = async (e) => {
  form.value.tokenId = e
  await captchaConfirmedRef.value.execute()
}

async function saveConfirmedCaptcha(e) {
  await catchFormErrors(
    currentResource.value.create
      .execute(
        {},
        profileTransformFrom({
          ...form.value,
          recaptcha: e,
        })
      )
      .then(
        ({
          data: {
            messages: [message],
            item,
          },
        }) => {
          window.dataLayer = window.dataLayer || []
          if (props.referer) {
            window.dataLayer.push({
              event: currentUserType.value.gtagEventForReferral,
              email: form.value.email,
            })
          } else {
            window.dataLayer.push({
              event: currentUserType.value.gtagEvent,
              email: form.value.email,
            })
          }
          const { open } = useModalOpener({
            name: 'UiMessageModal',
            component: UiMessageModal,
            message,
            redirect: {
              location: item?.isAuthenticated ? router.resolve({ name: 'cabinet' }).href : props.indexPageUrl,
            },
          })
          open()
        }
      )
      .catch((e) => {
        throw e
      }),
    formRef.value,
    defaultValidatorErrorsParser
  )
}

const send = async (sendData) => {
  try {
    const data = await currentResource.value.requestConfirmation.execute(
      {},
      {
        ...sendData,
      }
    )
    if (captchaRef.value) {
      captchaRef.value.reset()
    }
    return data
  } catch (e) {
    isLoad.value = false
    captchaRef.value.reset()
    return Promise.reject(e)
  }
}

const __returned__ = { TokenConfirmationCheck, UiMessageModal, router, profileTransformFrom, $t, userAgreement, privacyPolicy, props, formRef, form, captchaRef, CUSTOMER_TYPE, USER_ROLE_MAP, currentUserType, currentResource, onChangeRole, saveCaptcha, isLoad, getCaptcha, validateForm, currentCountryCode, countriesLoading, countriesList, countriesLoad, countriesSearch, countriesGet, languagesLoading, languagesList, languagesLoad, phoneRules, isNotRequiredRestField, FIELDS, request, captchaConfirmedRef, redirect, saveConfirmedCaptcha, send, computed, defineAsyncComponent, resolveDynamicComponent, ref, get useRouter() { return useRouter }, get geoApi() { return geoApi }, get accountApi() { return accountApi }, get useSubmit() { return useSubmit }, get catchFormErrors() { return catchFormErrors }, get defaultValidatorErrorsParser() { return defaultValidatorErrorsParser }, get useModalOpener() { return useModalOpener }, get useGlobal() { return useGlobal }, get useField() { return useField }, get useSelectsDynamicOptions() { return useSelectsDynamicOptions }, get composeTransformers() { return composeTransformers }, get simplifiers() { return simplifiers }, get GtagEvents() { return GtagEvents }, get registrationApi() { return registrationApi } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})